import { useEffect, useRef, useState } from 'react';

const isDev = process.env.NODE_ENV === 'development';
const CHECK_INTERVAL_MS = isDev ? 5_000 : 60_000;
const PENDING_UPDATE_KEY = 'taxi-app-pending-version';
const DISMISSED_UPDATE_KEY = 'taxi-app-dismissed-version';

const readStorage = (key) => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeStorage = (key, value) => {
  try {
    if (value) {
      sessionStorage.setItem(key, value);
    } else {
      sessionStorage.removeItem(key);
    }
  } catch {
    // Ignore private-mode / storage failures.
  }
};

const fetchRemoteVersion = async () => {
  const response = await fetch(`/version.json?_=${Date.now()}`, {
    cache: 'no-store',
    headers: { 'Cache-Control': 'no-cache' },
  });
  if (!response.ok) {
    throw new Error(`version.json HTTP ${response.status}`);
  }
  const data = await response.json();
  if (typeof data?.version !== 'string' || !data.version) {
    throw new Error('version.json missing version string');
  }
  return data.version;
};

/**
 * Polls /version.json and returns whether a newer build is available.
 * Once detected, the pending version is kept in sessionStorage so the
 * banner can show on login, /admin, and /maps for the rest of this tab.
 */
export const useAppUpdateCheck = () => {
  const bakedBuildId = process.env.REACT_APP_BUILD_ID || null;
  const baselineRef = useRef(bakedBuildId);
  const detectedRef = useRef(false);
  const [updateAvailable, setUpdateAvailable] = useState(() => {
    const pending = readStorage(PENDING_UPDATE_KEY);
    const dismissed = readStorage(DISMISSED_UPDATE_KEY);
    if (!pending || pending === bakedBuildId || pending === dismissed) {
      return false;
    }
    detectedRef.current = true;
    return true;
  });

  useEffect(() => {
    let cancelled = false;

    const checkForUpdate = async () => {
      if (cancelled) return;

      try {
        const remoteVersion = await fetchRemoteVersion();
        if (cancelled) return;

        if (bakedBuildId && remoteVersion === bakedBuildId) {
          writeStorage(PENDING_UPDATE_KEY, null);
          detectedRef.current = false;
          setUpdateAvailable(false);
          return;
        }

        if (!baselineRef.current) {
          baselineRef.current = remoteVersion;
          return;
        }

        if (remoteVersion === baselineRef.current) {
          return;
        }

        if (remoteVersion === readStorage(DISMISSED_UPDATE_KEY)) {
          return;
        }

        detectedRef.current = true;
        writeStorage(PENDING_UPDATE_KEY, remoteVersion);
        setUpdateAvailable(true);
      } catch (error) {
        if (isDev) {
          // eslint-disable-next-line no-console
          console.warn('[app-update] Check failed:', error);
        }
      }
    };

    checkForUpdate();
    const intervalId = setInterval(checkForUpdate, CHECK_INTERVAL_MS);

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        checkForUpdate();
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [bakedBuildId]);

  const dismissUpdate = () => {
    const pending = readStorage(PENDING_UPDATE_KEY);
    if (pending) {
      writeStorage(DISMISSED_UPDATE_KEY, pending);
    }
    writeStorage(PENDING_UPDATE_KEY, null);
    setUpdateAvailable(false);
  };

  const reloadWithUpdate = () => {
    writeStorage(PENDING_UPDATE_KEY, null);
    window.location.reload();
  };

  return { updateAvailable, dismissUpdate, reloadWithUpdate };
};

export default useAppUpdateCheck;
